import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "./LeafletMap.css";
import { Link } from "react-router-dom";

// Define the iconCreateFunction
const createClusterCustomIcon = (cluster) => {
  const markers = cluster.getAllChildMarkers();
  let clusterClass = "marker-cluster-small";

  // Customize the cluster class based on the markers inside
  if (markers.length > 50) {
    clusterClass = "marker-cluster-large";
  } else if (markers.length > 10) {
    clusterClass = "marker-cluster-medium";
  }

  return L.divIcon({
    html: `<p>${cluster.getChildCount()}</p>`,
    className: clusterClass,
    iconSize: L.point(40, 40, true),
  });
};

export default function MapComponent() {
  const defaultLocation = [49.6509441, 4.8428523];
  const [markers, setMarkers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestLocation, setRequestLocation] = useState(false);
  const StandardZoomLevel = 5;
  const databaseUrl = process.env.REACT_APP_DATABASE_URL;

  const fetchMarkers = async () => {
    try {
      const response = await fetch(databaseUrl);
      const data = await response.json();
      // Filter data to only include clubs with a valid position
      const filteredData = [];
      const seenHandles = new Set();
      data.forEach((club) => {
        if (
          club.location &&
          club.location.latitude !== undefined &&
          club.location.longitude !== undefined &&
          !seenHandles.has(club.handle)
        ) {
          filteredData.push(club);
          seenHandles.add(club.handle);
        }
      });
      setMarkers(filteredData); // Initially set all markers
    } catch (error) {
      console.error("Error fetching markers:", error);
    }
  };

  useEffect(() => {
    const checkGPS = () => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser");
        return;
      }
    };

    checkGPS();
  }, []);

  // Fix for default marker icon issue in Leaflet
  L.Icon.Default.mergeOptions({
    iconUrl:
      "https://mt.googleapis.com/vt/icon/name=icons/onion/SHARED-mymaps-container-bg_4x.png,icons/onion/SHARED-mymaps-container_4x.png,icons/onion/1799-golf-course_4x.png&highlight=ff000000,097138&scale=2.0",
    iconRetinaUrl:
      "https://mt.googleapis.com/vt/icon/name=icons/onion/SHARED-mymaps-container-bg_4x.png,icons/onion/SHARED-mymaps-container_4x.png,icons/onion/1799-golf-course_4x.png&highlight=ff000000,097138&scale=2.0",
    iconSize: [35, 35], // size of the icon
    iconAnchor: [17.5, 35], // point of the icon which will correspond to marker's location
    popupAnchor: [1, -34], // point from which the popup should open relative to the iconAnchor
  });

  // Get user location and set map view to user location if available
  const LocationFinder = () => {
    const map = useMapEvents({});

    useEffect(() => {
      if (requestLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = [
              position.coords.latitude,
              position.coords.longitude,
            ];
            map.setView(userLocation, 12); // zooms in on the user's location
            setRequestLocation(false); // Reset requestLocation after setting the location
            setSearchQuery(""); // Reset search query after setting the location
          },
          () => {
            map.setView(defaultLocation, StandardZoomLevel);
            setRequestLocation(false); // Reset requestLocation after setting the location
            setSearchQuery(""); // Reset search query after setting the location
          }
        );
      } else if (requestLocation) {
        map.setView(defaultLocation, StandardZoomLevel);
        setRequestLocation(false); // Reset requestLocation after setting the location
        setSearchQuery(""); // Reset search query after setting the location
      }
    }, [map]);

    return null;
  };

  useEffect(() => {
    fetchMarkers();
    // eslint-disable-next-line
  }, []);

  const SearchClub = ({ searchQuery }) => {
    const map = useMapEvents({});
    useEffect(() => {
      if (searchQuery) {
        const club = markers.find((marker) =>
          marker.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (club) {
          map.setView([club.location.latitude, club.location.longitude], 12);
        } else {
          // If club not found, search for city
          const fetchData = async () => {
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/search?city=${searchQuery}&format=json`
              );
              const data = await response.json();
              if (data.length > 0) {
                const city = data[0];
                map.setView([city.lat, city.lon], 12);
              } else {
                alert("Club or city not found");
              }
            } catch (error) {
              console.error("Error fetching city coordinates:", error);
            }
          };

          fetchData();
        }
        setSearchQuery(""); // Reset search query after search
      }
    }, [searchQuery, map]);

    return null;
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const searchQuery = event.target.elements.search.value;
    setSearchQuery(searchQuery);
  };

  const handleGetLocationClick = () => {
    setRequestLocation(true);
  };

  return (
    <div>
      <MapContainer
        style={{ height: "100vh", width: "100vw" }}
        center={defaultLocation}
        zoom={StandardZoomLevel}
      >
        <div className="map-controls">
          <button
            className="getLocationButton"
            onClick={handleGetLocationClick}
          >
            My Location
          </button>
          <form onSubmit={handleSearchSubmit}>
            <input
              type="text"
              name="search"
              placeholder="Search for clubs"
              className="map-search-input"
            />
            <button type="submit" className="map-search-input-button">
              Search
            </button>
          </form>
        </div>
        <TileLayer
          url="https://api.maptiler.com/maps/basic-v2/256/{z}/{x}/{y}.png?key=IrU9JY7a2c61HReO0i5c"
          attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
          maxZoom={16}
          minZoom={3}
        />
        <MarkerClusterGroup
          showCoverageOnHover={false}
          iconCreateFunction={createClusterCustomIcon}
        >
          {markers.length === 0 ? (
            <p>Loading...</p>
          ) : (
            <>
              {markers.map((marker) => (
                <Marker
                  key={marker.handle}
                  position={[
                    marker.location.latitude,
                    marker.location.longitude,
                  ]}
                >
                  <Popup>
                    <div>
                      <h3>{marker.name}</h3>
                      <Link to={`/${marker.handle}`}>More info</Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </>
          )}
        </MarkerClusterGroup>
        <LocationFinder />
        <SearchClub searchQuery={searchQuery} />
      </MapContainer>
    </div>
  );
}
